// ##### Extract date part (YYYY-MM-DD) from ISO string
export const toDateKey = (iso) => {
  if (!iso) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  if (iso.includes('T')) {
    const d = new Date(iso);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return '';
};

const DATE_KEY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Google-style exclusive end dates: add one calendar day from YYYY-MM-DD. */
export const dateKeyPlusOneDay = (dateKey) => {
  if (!DATE_KEY_REGEX.test(dateKey)) return '';
  const [, ys, ms, ds] = dateKey.match(DATE_KEY_REGEX);
  const d = new Date(Number(ys), Number(ms) - 1, Number(ds));
  d.setDate(d.getDate() + 1);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Google all-day ranges use exclusive end dates; subtract one calendar day from YYYY-MM-DD. */
export const dateKeyMinusOneDay = (dateKey) => {
  if (!DATE_KEY_REGEX.test(dateKey)) return '';
  const [, ys, ms, ds] = dateKey.match(DATE_KEY_REGEX);
  const d = new Date(Number(ys), Number(ms) - 1, Number(ds));
  d.setDate(d.getDate() - 1);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Prefer serializer `is_all_day` when present — Google payloads may omit empty datetimes inconsistently. */
export const eventIsAllDay = (event) =>
  Boolean(event?.is_all_day) || (!event?.start_datetime && !!event?.start_date);

export const eventStartKey = (event) => {
  if (!event) return '';
  if (eventIsAllDay(event) && event.start_date) {
    return toDateKey(event.start_date);
  }
  return toDateKey(event.start_datetime || event.start_date || '');
};

/** Last calendar day the event occupies (inclusive). */
export const eventEndInclusiveKey = (event) => {
  const startKey = eventStartKey(event);
  if (!startKey) return '';
  if (eventIsAllDay(event)) {
    if (!event.end_date) return startKey;
    const exclusiveEnd = toDateKey(event.end_date);
    if (!exclusiveEnd) return startKey;
    const last = dateKeyMinusOneDay(exclusiveEnd);
    return last && last >= startKey ? last : startKey;
  }
  return (
    toDateKey(
      event.end_datetime || event.end_date || event.start_datetime || event.start_date
    ) || startKey
  );
};

export const isMultiDayEvent = (event) => {
  const startKey = eventStartKey(event);
  if (!startKey) return false;
  const endInc = eventEndInclusiveKey(event);
  return !!(endInc && endInc !== startKey);
};

/**
 * Como desenhar um evento multi-dias num dia concreto da grelha mensal (fragmentos entre células).
 * Dia de início: faixa forte à esquerda, canto esquerdo arredondado, título visível.
 * Dias intermédios: sem texto/hora nem acento forte à esquerda; cantos retos até ao último dia.
 */
export function getMonthCellMultiDaySegment(event, dateKey) {
  const defaults = {
    hideInlineLabel: false,
    strongLeftAccent: true,
    roundLeft: true,
    roundRight: true,
  };
  if (!event || !dateKey || !isMultiDayEvent(event)) return defaults;

  const sk = eventStartKey(event);
  const ek = eventEndInclusiveKey(event);
  if (!sk || !ek || !isEventOnDate(event, dateKey)) return defaults;

  const isFirstCalendarDay = dateKey === sk;
  const isLastCalendarDay = dateKey === ek;
  return {
    hideInlineLabel: !isFirstCalendarDay,
    strongLeftAccent: isFirstCalendarDay,
    roundLeft: isFirstCalendarDay,
    roundRight: isLastCalendarDay,
  };
}

/** Event occurs on calendar day dateKey */
export const isEventOnDate = (event, dateKey) => {
  const startKey = eventStartKey(event);
  if (!startKey) return false;
  const endInc = eventEndInclusiveKey(event);
  const endKey = endInc || startKey;

  if (startKey === endKey) return dateKey === startKey;

  return dateKey >= startKey && dateKey <= endKey;
};

export const datesToWeekKeys = (week) =>
  week.map((d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);

/** Stable empty — do not mutate. Use with `buildEventsByDateKeyIndex` lookups. */
export const PLANNER_EMPTY_DAY_EVENTS_LIST = Object.freeze([]);

const MAX_DAYS_PER_EVENT_SPAN = 4000;

/**
 * Map YYYY-MM-DD → event[] for O(1) cell lookup (avoids filtering the full list per day cell).
 * @param {object[]|null|undefined} events
 * @returns {Map<string, object[]>}
 */
export function buildEventsByDateKeyIndex(events) {
  const map = new Map();
  if (!Array.isArray(events) || events.length === 0) return map;

  for (const ev of events) {
    const sk = eventStartKey(ev);
    if (!sk) continue;
    const ek = eventEndInclusiveKey(ev) || sk;

    let k = sk;
    let guard = 0;
    while (k && k <= ek && guard < MAX_DAYS_PER_EVENT_SPAN) {
      const bucket = map.get(k);
      if (bucket) bucket.push(ev);
      else map.set(k, [ev]);
      const next = dateKeyPlusOneDay(k);
      if (!next || next <= k) break;
      k = next;
      guard += 1;
    }
  }
  return map;
}
